import axios from 'axios';
import { sollAbmelden, tokenAusHeader } from './sitzung';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - skip redirect for login/auth endpoints
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register') || requestUrl.includes('/auth/forgot-password') || requestUrl.includes('/auth/reset-password') || requestUrl.includes('/auth/2fa/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Lief die Anfrage noch mit einem ALTEN Token, liegt schon ein neues im Speicher -> bleiben.
      const abmelden = await sollAbmelden(
        tokenAusHeader(error.config?.headers),
        () => localStorage.getItem('token'),
        (ms) => new Promise((fertig) => setTimeout(fertig, ms)),
      );
      if (abmelden) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password, geraetMerker) => api.post('/auth/login', { email, password, ...(geraetMerker ? { geraet_merker: geraetMerker } : {}) }),
  sitzung: () => api.post('/auth/sitzung'),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Users API (Admin)
export const usersAPI = {
  getAll: (status = 'active') => api.get(`/admin/users?status=${status}`),
  getAllRoles: () => api.get('/admin/users/all-roles'),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  archive: (id) => api.post(`/admin/users/${id}/archive`),
  unarchive: (id) => api.post(`/admin/users/${id}/unarchive`),
  sendResetLink: (id) => api.post(`/admin/users/${id}/reset-password`, {}),
  createAccessCode: (id) => api.post(`/admin/users/${id}/access-code`, {}),
  revokeAccess: (id) => api.post(`/admin/users/${id}/revoke-access`, {}),
  onboardingStatus: () => api.get('/admin/onboarding-status'),
  sperren: (id, grund) => api.post(`/admin/users/${id}/sperren`, grund && grund.trim() ? { grund: grund.trim() } : {}),
  entsperren: (id) => api.post(`/admin/users/${id}/entsperren`),
  sitzungenBeenden: (id) => api.post(`/admin/users/${id}/sitzungen-beenden`),
  zweiFaktorZuruecksetzen: (id) => api.post(`/admin/users/${id}/2fa/zuruecksetzen`),
};

// Assignments API
export const assignmentsAPI = {
  getAll: () => api.get('/assignments'),
  create: (data) => api.post('/admin/assignments', data),
  update: (id, data) => api.put(`/admin/assignments/${id}`, data),
  delete: (id) => api.delete(`/admin/assignments/${id}`),
  deactivate: (id) => api.post(`/admin/assignments/${id}/deactivate`),
  reactivate: (id) => api.post(`/admin/assignments/${id}/reactivate`),
  getExpiring: () => api.get('/admin/assignments/expiring'),
  getMonthlyTarget: (id, month, year) => api.get(`/assignments/${id}/monthly-target`, { params: { month, year } }),
};

// Timesheets API
export const timesheetsAPI = {
  getAll: () => api.get('/timesheets'),
  getOne: (id) => api.get(`/timesheets/${id}`),
  lexwareErneut: (id) => api.post(`/admin/timesheets/${id}/lexware-erneut`),
  create: (data) => api.post('/timesheets', data),
  createForEmployee: (data) => api.post('/admin/timesheets/create-for-employee', data),
  updateEntries: (id, entries) => api.put(`/timesheets/${id}/entries`, entries),
  submit: (id, signatures) => api.post(`/timesheets/${id}/submit`, signatures),
  reopen: (id, reason) => api.post(`/admin/timesheets/${id}/reopen`, { reason: reason || '' }),
  getHistory: (id) => api.get(`/admin/timesheets/${id}/history`),
  restoreVersion: (id, index) => api.post(`/admin/timesheets/${id}/restore/${index}`),
  getPDF: (id) => api.get(`/timesheets/${id}/pdf`),
  markBillingSent: (id, date) => api.post(`/admin/timesheets/${id}/billing-sent`, { date, confirmed: true }),
  unmarkBillingSent: (id) => api.post(`/admin/timesheets/${id}/billing-unsent`),
  requestTeacherConfirmation: (id, teacherEmail) => api.post(`/timesheets/${id}/request-teacher-confirmation`, { teacher_email: teacherEmail }),
  updateSonderstunden: (id, sonderstunden) => api.put(`/timesheets/${id}/sonderstunden`, sonderstunden),
  getAbrechnungPDF: (id) => api.get(`/timesheets/${id}/abrechnung-pdf`),
  updateMeta: (id, data) => api.put(`/admin/timesheets/${id}/meta`, data),
  archive: (id) => api.post(`/admin/timesheets/${id}/archive`),
  restore: (id) => api.post(`/admin/timesheets/${id}/restore`),
  getArchived: () => api.get('/admin/timesheets/archived'),
};

// Audit / Ereignis-Protokoll
export const auditAPI = {
  get: (entityType, entityId) => api.get(`/audit/${entityType}/${entityId}`),
};

// Dashboard API
export const dashboardAPI = {
  getAdminDashboard: (month, year) => 
    api.get('/admin/dashboard', { params: { month, year } }),
  getDashboardPDF: (month, year) => api.get(`/admin/dashboard/pdf?month=${month}&year=${year}`),
  getDashboardExcel: (month, year) => api.get(`/admin/dashboard/excel?month=${month}&year=${year}`),
  getYearlyOverview: (year) => api.get(`/admin/yearly-overview?year=${year}`),
  getYearlyOverviewPDF: (year, assignmentId) => api.get('/admin/yearly-overview/pdf', { params: { year, assignment_id: assignmentId } }),
  getYearlyAIReport: (year, assignmentId) => api.post('/admin/yearly-overview/ai-report', null, { params: { year, assignment_id: assignmentId } }),
};

// Settings API
export const settingsAPI = {
  getSMTP: () => api.get('/admin/settings/smtp'),
  saveSMTP: (config) => api.post('/admin/settings/smtp', config),
  updateSMTP: (config) => api.put('/admin/settings/smtp', config),
  testSMTP: () => api.post('/admin/settings/smtp/test'),
  getBranding: () => api.get('/settings/branding'),
  updateBranding: (data) => api.put('/admin/settings/branding', data),
  getSystemDiagnostics: () => api.get('/admin/system-diagnostics'),
  getNotifications: () => api.get('/admin/settings/notifications'),
  updateNotifications: (data) => api.put('/admin/settings/notifications', data),
  getReminders: () => api.get('/admin/settings/reminders'),
  updateReminders: (data) => api.put('/admin/settings/reminders', data),
  runRemindersNow: (stufe = 'vorwarnung', dryRun = true, confirm = '') => api.post(
    '/admin/reminders/run-now', null,
    { params: confirm ? { stufe, dry_run: dryRun, confirm } : { stufe, dry_run: dryRun } }),
  getReminderAusloesetage: () => api.get('/admin/reminders/ausloesetage'),
  getSchoolHolidays: () => api.get('/admin/settings/school-holidays'),
  getSchoolHolidaysStatus: () => api.get('/admin/settings/school-holidays/status'),
  updateSchoolHolidays: (state, list) => api.put(`/admin/settings/school-holidays/${state}`, list),
  getLexware: () => api.get('/admin/settings/lexware'),
  updateLexware: (data) => api.put('/admin/settings/lexware', data),
  lexwareDiagnose: (timesheetId, create) =>
    api.post('/admin/settings/lexware/diagnose', { timesheet_id: timesheetId || null, create: !!create }),
  startLexwareVoucherReport: (body) => api.post('/admin/settings/lexware/voucher-report', body || {}),
  getLexwareVoucherReport: () => api.get('/admin/settings/lexware/voucher-report'),
  resetLexwareVoucherFields: (body) => api.post('/admin/settings/lexware/voucher-report/reset', body || {}),
  getAIConfigs: () => api.get('/admin/ai-configs'),
  updateAIConfig: (id, body) => api.put(`/admin/ai-configs/${id}`, body),
  testAIConfig: (id, body) => api.post(`/admin/ai-configs/${id}/test`, body),
  updateAiBudget: (body) => api.put('/admin/ai-budget', body),
  zweiFaktorPflicht: () => api.get('/admin/settings/zwei-faktor-pflicht'),
  zweiFaktorPflichtSetzen: (aktiv) => api.put('/admin/settings/zwei-faktor-pflicht', { aktiv }),
};

// Backup API
export const backupAPI = {
  speicherProtokoll: () => api.get('/admin/speicher-protokoll', { params: { tage: 14 } }),
  create: () => api.post('/admin/backup/create', null, { responseType: 'blob' }),
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/backup/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  restore: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/backup/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  cloudStatus: () => api.get('/admin/backup/cloud/status'),
  cloudJetzt: () => api.post('/admin/backup/cloud/jetzt'),
  cloudDownload: (schluessel) => api.get('/admin/backup/cloud/download', {
    params: { schluessel }, responseType: 'blob' }),
  cloudTestAlarm: () => api.post('/admin/backup/cloud/test-alarm'),
};

// Roles API
export const rolesAPI = {
  getAll: () => api.get('/roles'),
};

// Federal States API
export const federalStatesAPI = {
  getAll: () => api.get('/federal-states'),
  getHolidays: (state, year) => api.get(`/holidays/${state}`, { params: { year } }),
};

// Jugendaemter API
export const jugendaemterAPI = {
  getAll: () => api.get('/admin/jugendaemter'),
  create: (data) => api.post('/admin/jugendaemter', data),
  update: (id, data) => api.put(`/admin/jugendaemter/${id}`, data),
  delete: (id) => api.delete(`/admin/jugendaemter/${id}`),
};

// Assistent API
export const assistantAPI = {
  health: () => api.get('/assistant/health'),
  unsubmitted: (month, year) =>
    api.get('/assistant/quick/unsubmitted', { params: { month, year } }),
  lookup: (q, limit = 8) =>
    api.get('/assistant/lookup', { params: { q, limit } }),
  chat: (message, conversationId) =>
    api.post('/assistant/chat', { message, conversation_id: conversationId }),
  conversations: () => api.get('/assistant/conversations'),
  conversation: (id) => api.get(`/assistant/conversations/${id}`),
  limitStatus: () => api.get('/assistant/limits/status'),
  getLimits: () => api.get('/assistant/admin/limits'),
  setLimits: (messagesPerDay) =>
    api.put('/assistant/admin/limits', { messages_per_day: messagesPerDay }),
  favorites: () => api.get('/assistant/favorites'),
  saveFavorites: (items) => api.put('/assistant/favorites', { items }),
  feedback: (conversationId, turnId, rating) =>
    api.post('/assistant/feedback', { conversation_id: conversationId, turn_id: turnId, rating }),
  event: (conversationId, turnId, type, target) =>
    api.post('/assistant/event', { conversation_id: conversationId, turn_id: turnId, type, target }),
  adminChatlogs: (q, employeeId) =>
    api.get('/assistant/admin/chatlogs', { params: { q, employee_id: employeeId } }),
  adminChatlogEmployees: () => api.get('/assistant/admin/chatlogs/employees'),
  adminChatlogDetail: (id) => api.get(`/assistant/admin/chatlogs/conversation/${id}`),
  adminChatlogExport: (employeeId, fmt) =>
    api.get('/assistant/admin/chatlogs-export', {
      params: { employee_id: employeeId || '', fmt },
      responseType: fmt === 'csv' ? 'blob' : 'json',
    }),
  usageAnalytics: () => api.get('/assistant/admin/usage'),
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append('audio', blob, 'audio.webm');
    return api.post('/assistant/transcribe', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data.text);
  },
};

export default api;

// Public API (no auth interceptor — for teacher confirmation)
const publicApi = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' } });

export const teacherConfirmAPI = {
  get: (token) => publicApi.get(`/public/teacher-confirm/${token}`),
  confirm: (token, name, signature) => publicApi.post(`/public/teacher-confirm/${token}`, { teacher_name: name, teacher_signature: signature }),
};

// Global Search API
export const searchAPI = {
  global: (q, limitPerType = 10) =>
    api.get('/search/global', { params: { q, limit_per_type: limitPerType } }),
};
